if (!customElements.get('complementary-product-slider')) {
  customElements.define(
    'complementary-product-slider',
    class ComplementaryProductSlider extends HTMLElement {
      constructor() {
        super();

        this.sliderOptions = {
          slidesPerView: '1',
          spaceBetween: 10,
          loop: false,
          grabCursor: false,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            prevEl: this.querySelector('.swiper-button-prev'),
            nextEl: this.querySelector('.swiper-button-next'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
            formatFractionCurrent: (number) => {
              // Return the correct 1-based index that matches the actual products
              return ((number - 1) % this.productCount) + 1;
            },
            formatFractionTotal: (number) => {
              // Return the actual number of products, not the inflated slide count (which may include duplicates)
              return this.productCount || number;
            }
          },
          on: {
            init: (swiper) => {
              // console.log('Swiper initialized with', this.productCount, 'products');
              this.updateProductCardPagination(swiper);
              this.updateProductCardButtonStates(swiper);
              this.dispatchEvent(new CustomEvent('complementary-slider:initialized', {
                bubbles: true,
                detail: { swiper }
              }));
            },
            slideChange: (swiper) => {
              this.updateProductCardPagination(swiper);
              this.updateProductCardButtonStates(swiper);
            }
          }
        };
      }

      connectedCallback() {
        // Get and store the product count as a property
        this.setProductCount();
        
        // Initialize the slider
        const slider = this.querySelector('.swiper');
        if (!slider) return;

        this.sliderInstance = new window.FoxTheme.Carousel(slider, this.sliderOptions);
        this.sliderInstance.init();

        this.fixQuickviewDuplicate();
      }

      /**
       * Updates the button states in all product cards based on slider position
       */
      updateProductCardButtonStates(swiper) {
        if (!swiper) return;
        
        // Find all product card prev/next buttons
        const prevButtons = document.querySelectorAll('.product-card__nav-prev');
        const nextButtons = document.querySelectorAll('.product-card__nav-next');
        
        // For non-loop sliders, disable buttons when at beginning/end
        if (!swiper.params.loop) {
          // Update previous buttons (disabled at beginning)
          prevButtons.forEach(button => {
            if (swiper.isBeginning) {
              button.classList.add('disabled');
              button.setAttribute('aria-disabled', 'true');
            } else {
              button.classList.remove('disabled');
              button.setAttribute('aria-disabled', 'false');
            }
          });
          
          // Update next buttons (disabled at end)
          nextButtons.forEach(button => {
            if (swiper.isEnd) {
              button.classList.add('disabled');
              button.setAttribute('aria-disabled', 'true');
            } else {
              button.classList.remove('disabled');
              button.setAttribute('aria-disabled', 'false');
            }
          });
        } else {
          // If loop is enabled, make sure buttons aren't disabled
          prevButtons.forEach(button => {
            button.classList.remove('disabled');
            button.setAttribute('aria-disabled', 'false');
          });
          nextButtons.forEach(button => {
            button.classList.remove('disabled');
            button.setAttribute('aria-disabled', 'false');
          });
        }
      }

      /**
       * Updates the pagination display in all product cards
       */
      updateProductCardPagination(swiper) {
        if (!swiper) return;
        
        // Calculate the correct current slide and total slides
        const currentSlide = ((swiper.realIndex) % this.productCount) + 1;
        const totalSlides = this.productCount;
        
        // Update all current-slide elements in product cards
        document.querySelectorAll('.product-card__nav-count .current-slide').forEach(el => {
          el.textContent = currentSlide;
        });
        
        // Update all total-slides elements in product cards
        document.querySelectorAll('.product-card__nav-count .total-slides').forEach(el => {
          el.textContent = totalSlides;
        });
      }

      /**
       * Get the product count from the most reliable source available
       */
      setProductCount() {
        // Try to get from parent element's dataset first (from template)
        if (this.closest('.product__block')) {
          const count = parseInt(this.closest('.product__block').dataset.complementaryCount || 0, 10);
          if (count > 0) {
            this.productCount = count;
            this.dataset.slideCount = count; // For backwards compatibility
            return;
          }
        }
        
        // Count the actual products with the data-complementary-product attribute
        const productCards = this.querySelectorAll('[data-complementary-product="true"]');
        if (productCards.length > 0) {
          this.productCount = productCards.length;
          this.dataset.slideCount = this.productCount;
          return;
        }
        
        // Fallback to non-duplicate slides in the swiper wrapper
        if (this.querySelector('.swiper-wrapper')) {
          const nonDuplicateSlides = this.querySelectorAll('.swiper-wrapper > .swiper-slide:not(.swiper-slide-duplicate)');
          if (nonDuplicateSlides.length > 0) {
            this.productCount = nonDuplicateSlides.length;
            this.dataset.slideCount = this.productCount;
            return;
          }
        }
        
        // Default fallback
        this.productCount = 3;
        this.dataset.slideCount = this.productCount;

        // Also update all product card pagination with the correct total
        document.querySelectorAll('.product-card__nav-count .total-slides').forEach(el => {
          el.textContent = this.productCount;
        });
      }

      /**
       * Updates all pagination controls with current values
       */
      updatePaginationControls(swiper) {
        if (!swiper) return;
        
        // Make sure product count is set
        if (!this.productCount) {
          this.setProductCount();
        }
        
        // Force Swiper to re-render pagination if it exists
        if (swiper.pagination && typeof swiper.pagination.render === 'function') {
          swiper.pagination.render();
          swiper.pagination.update();
        }

        // Also update the product card pagination and button states
        this.updateProductCardPagination(swiper);
        this.updateProductCardButtonStates(swiper);
      }

      fixQuickviewDuplicate() {
        let modalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const modalID = modal.getAttribute('id');
          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }
    }
  );
}
